import { useState, useEffect } from "react";

interface ViewportSize {
  width: number;
  height: number;
}

function readViewportSize(): ViewportSize {
  return { width: window.innerWidth, height: window.innerHeight };
}

/**
 * Tracks the browser viewport dimensions, updating live as the window resizes.
 * Used by the dev menu's Viewport row so the team can read and copy their exact
 * viewport when discussing how UI fits at a given size (ENG-5877).
 *
 * `enabled` gates the resize listener. The Viewport label only shows while the
 * user menu is open; a menu that stays mounted while closed would otherwise
 * re-render on every resize event (60+ Hz during a drag-resize) for a label
 * nobody can see. When enabled we sync once (in case the window changed while
 * we weren't listening) and coalesce resize bursts to one render per frame.
 */
export function useViewportSize(enabled: boolean = true): ViewportSize {
  const [size, setSize] = useState<ViewportSize>(readViewportSize);

  useEffect(() => {
    if (!enabled) return;
    setSize(readViewportSize());

    let rafId = 0;
    function handleResize() {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => setSize(readViewportSize()));
    }
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(rafId);
    };
  }, [enabled]);

  return size;
}
